import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import useAdminAuth from '../hooks/useAdminAuth';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  TrendingUp,
  Package,
  Users,
  DollarSign,
  MessageSquare,
  Truck,
  Building,
  CheckCircle,
  Clock,
  Calendar,
  BarChart3
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  useAdminAuth(); // Protect this route
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({
    loads: { total: 0, open: 0, inTransit: 0, completed: 0, accepted: 0, expired: 0, byMonth: [] },
    quotes: { total: 0, pending: 0, approved: 0, rejected: 0 },
    users: { total: 0, shippers: 0, carriers: 0, newThisMonth: 0 },
    revenue: { totalQuoteValue: 0, averageLoadValue: 0, approvedQuotes: 0 }
  });

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      // Fetch all data
      const [loadsRes, quotesRes, usersRes] = await Promise.all([
        axios.get(`${API}/loads`),
        axios.get(`${API}/admin/quotes/pending`),
        axios.get(`${API}/users`)
      ]);

      const loads = loadsRes.data;
      const quotes = quotesRes.data;
      const users = usersRes.data;

      // Calculate load statistics
      const loadStats = {
        total: loads.length,
        open: loads.filter(l => ['pending', 'quoted'].includes(l.status)).length,
        inTransit: loads.filter(l => l.status === 'in_transit').length,
        completed: loads.filter(l => l.status === 'delivered').length,
        accepted: loads.filter(l => ['accepted', 'awaiting_adhoc_payment', 'awaiting_shipper_payment', 'paid'].includes(l.status)).length,
        expired: loads.filter(l => l.status === 'expired').length,
        byMonth: calculateMonthlyLoads(loads)
      };

      // Calculate quote statistics
      const quoteStats = {
        total: quotes.length,
        pending: quotes.filter(q => q.status === 'pending').length,
        approved: quotes.filter(q => q.status === 'approved').length,
        rejected: quotes.filter(q => q.status === 'rejected').length
      };

      // Calculate user statistics
      const thisMonth = new Date();
      thisMonth.setDate(1);
      thisMonth.setHours(0, 0, 0, 0);

      const userStats = {
        total: users.length,
        shippers: users.filter(u => (u.roles && u.roles.includes('shipper')) || u.role === 'shipper').length,
        carriers: users.filter(u => (u.roles && u.roles.includes('carrier')) || u.role === 'carrier').length,
        newThisMonth: users.filter(u => new Date(u.created_at) >= thisMonth).length
      };

      // Calculate revenue statistics (subscription-based, no commissions)
      const approvedQuotes = quotes.filter(q => q.status === 'approved');
      const totalQuoteValue = approvedQuotes.reduce((sum, q) => {
        return sum + (parseFloat(q.amount) || parseFloat(q.original_amount) || 0);
      }, 0);

      const averageLoadValue = loads.length > 0
        ? loads.reduce((sum, l) => sum + (parseFloat(l.budget) || 0), 0) / loads.length
        : 0;

      const revenueStats = {
        totalQuoteValue: totalQuoteValue.toFixed(2),
        averageLoadValue: averageLoadValue.toFixed(2),
        approvedQuotes: approvedQuotes.length
      };

      setAnalytics({
        loads: loadStats,
        quotes: quoteStats,
        users: userStats,
        revenue: revenueStats
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load analytics',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateMonthlyLoads = (loads) => {
    const months = {};
    loads.forEach(load => {
      const date = new Date(load.created_at);
      const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months[monthYear] = (months[monthYear] || 0) + 1;
    });
    return Object.entries(months).slice(-6); // Last 6 months
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <p>Loading analytics...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#1e3a5f] flex items-center gap-3">
                <BarChart3 className="w-8 h-8" />
                Analytics Dashboard
              </h1>
              <p className="text-gray-600 mt-2">Platform performance and insights</p>
            </div>
            <Button
              onClick={() => navigate('/admin')}
              variant="outline"
              className="border-gray-300"
            >
              Back to Dashboard
            </Button>
          </div>

          {/* Overview Cards */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Platform Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-800 font-semibold">Total Loads</p>
                    <p className="text-4xl font-bold text-blue-900">{analytics.loads.total}</p>
                  </div>
                  <Package className="w-12 h-12 text-blue-600" />
                </div>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-800 font-semibold">Total Users</p>
                    <p className="text-4xl font-bold text-green-900">{analytics.users.total}</p>
                  </div>
                  <Users className="w-12 h-12 text-green-600" />
                </div>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-800 font-semibold">Total Quotes</p>
                    <p className="text-4xl font-bold text-purple-900">{analytics.quotes.total}</p>
                  </div>
                  <MessageSquare className="w-12 h-12 text-purple-600" />
                </div>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-orange-50 to-orange-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-800 font-semibold">Active Loads</p>
                    <p className="text-4xl font-bold text-orange-900">{analytics.loads.open}</p>
                  </div>
                  <Truck className="w-12 h-12 text-orange-600" />
                </div>
              </Card>
            </div>
          </div>

          {/* Load Statistics */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Load Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Open Loads</p>
                    <p className="text-3xl font-bold text-green-600">{analytics.loads.open}</p>
                  </div>
                  <Package className="w-10 h-10 text-green-600" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">In Transit</p>
                    <p className="text-3xl font-bold text-yellow-600">{analytics.loads.inTransit}</p>
                  </div>
                  <Truck className="w-10 h-10 text-yellow-600" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Completed</p>
                    <p className="text-3xl font-bold text-purple-600">{analytics.loads.completed}</p>
                  </div>
                  <CheckCircle className="w-10 h-10 text-purple-600" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Avg Load Value</p>
                    <p className="text-2xl font-bold text-blue-600">${analytics.revenue.averageLoadValue}</p>
                  </div>
                  <DollarSign className="w-10 h-10 text-blue-600" />
                </div>
              </Card>
            </div>
          </div>

          {/* User Statistics */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">User Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Shippers</p>
                    <p className="text-3xl font-bold text-green-600">{analytics.users.shippers}</p>
                  </div>
                  <Building className="w-10 h-10 text-green-600" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Carriers</p>
                    <p className="text-3xl font-bold text-orange-600">{analytics.users.carriers}</p>
                  </div>
                  <Truck className="w-10 h-10 text-orange-600" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">New This Month</p>
                    <p className="text-3xl font-bold text-blue-600">{analytics.users.newThisMonth}</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-blue-600" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Growth Rate</p>
                    <p className="text-3xl font-bold text-purple-600">
                      {((analytics.users.newThisMonth / Math.max(analytics.users.total - analytics.users.newThisMonth, 1)) * 100).toFixed(1)}%
                    </p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-purple-600" />
                </div>
              </Card>
            </div>
          </div>

          {/* Quote Statistics */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Quote Management</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Pending Quotes</p>
                    <p className="text-3xl font-bold text-yellow-600">{analytics.quotes.pending}</p>
                  </div>
                  <Clock className="w-10 h-10 text-yellow-600" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Approved Quotes</p>
                    <p className="text-3xl font-bold text-green-600">{analytics.quotes.approved}</p>
                  </div>
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Approval Rate</p>
                    <p className="text-3xl font-bold text-blue-600">
                      {analytics.quotes.total > 0
                        ? ((analytics.quotes.approved / analytics.quotes.total) * 100).toFixed(1)
                        : 0}%
                    </p>
                  </div>
                  <BarChart3 className="w-10 h-10 text-blue-600" />
                </div>
              </Card>
            </div>
          </div>

          {/* Revenue Statistics */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Quote & Load Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-800 font-semibold">Total Quote Value</p>
                    <p className="text-3xl font-bold text-green-900">${analytics.revenue.totalQuoteValue}</p>
                  </div>
                  <DollarSign className="w-10 h-10 text-green-600" />
                </div>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-800 font-semibold">Avg Load Value</p>
                    <p className="text-3xl font-bold text-blue-900">${analytics.revenue.averageLoadValue}</p>
                  </div>
                  <Package className="w-10 h-10 text-blue-600" />
                </div>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-800 font-semibold">Approved Quotes</p>
                    <p className="text-3xl font-bold text-purple-900">{analytics.revenue.approvedQuotes}</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-purple-600" />
                </div>
              </Card>
            </div>
          </div>

          {/* Monthly Load Trend */}
          {analytics.loads.byMonth.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Load Trend (Last 6 Months)</h2>
              <Card className="p-6">
                <div className="flex items-end justify-around h-64 border-b border-gray-300">
                  {analytics.loads.byMonth.map(([month, count]) => (
                    <div key={month} className="flex flex-col items-center">
                      <div
                        className="bg-blue-600 w-16 rounded-t"
                        style={{ height: `${(count / Math.max(...analytics.loads.byMonth.map(m => m[1]))) * 200}px` }}
                      ></div>
                      <p className="text-xs text-gray-600 mt-2">{month.split('-')[1]}/{month.split('-')[0].slice(2)}</p>
                      <p className="text-sm font-bold text-gray-900">{count}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default AdminAnalytics;
